import { useMemo } from 'react';
import { useQuery } from 'react-query';
import useUserOrganizationId from '../../../hooks/useUserOrganizationId';
import isNil from 'lodash/isNil';
import UnitFloorPlanHistoryService from '../../../services/unitFloorPlanHistoryService';
import UnitService from '../../../services/unitService';

const formatChangedBy = (changedBy) =>
  changedBy ? `${changedBy.firstName} ${changedBy.lastName}` : '---';

export function useUnitHistory({
  floorPlan,
  unitId,
  unitCreatedAt,
  history,
  propertyId,
}) {
  const { data, isLoading } = useQuery(
    ['unitHistory', unitId, floorPlan?.id],
    () => {
      const service = new UnitFloorPlanHistoryService();
      return service.getByUnitId(unitId);
    },
    {
      cacheTime: 0,
      refetchOnWindowFocus: false,
    },
  );
  const rows = useMemo(() => {
    if (isLoading && isNil(data)) return [];

    // Each history record represents the floor plan that was *replaced* on
    // `changedOn` (the old value), by `changedBy`. The currently-active floor
    // plan is never in this list because it has not been replaced yet, so it is
    // provided separately and prepended as the first row.
    const sorted = [...(data ?? [])].sort(
      (a, b) => new Date(b.changedOn) - new Date(a.changedOn),
    );

    const result = [];

    if (floorPlan) {
      const latestChange = sorted[0];
      result.push({
        id: floorPlan.id,
        floorPlanId: floorPlan.id,
        floorPlan: floorPlan.internalName ?? floorPlan.name ?? '',
        // Matches how history rows snapshot set-aside (flat FloorPlan.unitRentRestriction).
        setAside: floorPlan.unitRentRestriction ?? null,
        startDate: latestChange?.changedOn ?? null,
        endDate: null,
        changedBy: formatChangedBy(latestChange?.changedBy),
      });
    }

    sorted.forEach((d, index) => {
      // The assignment of this (replaced) floor plan started with the previous,
      // older change, and was made by that change's user. The oldest row has no
      // earlier change, so it started when the unit was created.
      const previousChange = sorted[index + 1];
      const isOldest = index === sorted.length - 1;
      result.push({
        id: d.id,
        floorPlanId: d.floorPlanId,
        floorPlan: d.name,
        setAside: d.unitRentRestriction,
        startDate:
          previousChange?.changedOn ??
          (isOldest ? unitCreatedAt ?? null : null),
        endDate: d.changedOn,
        changedBy: formatChangedBy(previousChange?.changedBy),
      });
    });

    return result;
  }, [data, isLoading, floorPlan, unitCreatedAt]);

  return {
    rows,
    onFloorPlanClick: (floorPlanId?: string) => {
      if (isNil(floorPlanId)) return;

      history.push(`/property/${propertyId}/floorplan/${floorPlanId}`);
    },
    isLoading,
  };
}

export function useHouseholdUnitHistory({ unitId, propertyId, history }) {
  const organizationId = useUserOrganizationId();
  const unitService = useMemo(() => new UnitService(), []);
  const { data, isLoading, isError } = useQuery(
    ['unitHouseholdHistory', unitId],
    () => {
      return unitService.getHouseholdUnitHistory({
        unitId,
        propertyId,
        organizationId,
      });
    },
    {
      cacheTime: 0,
      refetchOnWindowFocus: false,
    },
  );

  const rows = useMemo(() => {
    if (isLoading && isNil(data)) return [];

    let householdUnits = [];
    if (Array.isArray(data)) {
      householdUnits = data;
    } else if (Array.isArray(data?.householdUnits)) {
      householdUnits = data.householdUnits;
    }

    return householdUnits.map((d) => ({
      id: d.id,
      residentId: d?.household?.mainCus?.rc?.residentId,
      moveInDate: d?.moveInDate,
      moveOutDate: d?.moveOutDate,
      residentName: `${d?.household?.mainCus?.firstName} ${d?.household?.mainCus?.lastName}`,
      isTransfer: d?.isTransfer || false,
      floorPlanName: d?.floorPlanName ?? '',
    }));
  }, [data, isLoading]);

  return {
    rows,
    onResidentClick: (residentId: string) => {
      history.push(`/property/${propertyId}/prior-resident/${residentId}`);
    },
    isLoading,
    isError,
  };
}
